import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Card, Row, Spin, Tooltip, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useDataAccess, NO_PERMISSION_LABEL } from "@components/PermissionedComponents";
import { getDropdownKey, getMetricDropdownLabel, isAssignedMetric, get_metric_info_from_identifier } from "../../Utils/chart_utils";
import { trainingAPI } from "../../api/TrainingAPI";
import CompactStatisticNumber from "./CompactStaticNumber";
import { requestStatTile } from "@store/actions/statTiles.actions";
import "../../Styles/styles-Profile.css";



export const CompactStatTile = ({
  dropdowns,
  metric_identifier_with_stat,
  onChange = () => {},
  metricType,
  stat,
  option,
  endDate,
  startDate,
  playerId,
  type,
  positionFilter
}) => {
  const {
    filter: { position, player_group },
    all_metric_types,
    helmet_metrics,
    metrics,
    positionEntities
  } = useSelector(({ team }) => team);
  const dispatch = useDispatch();
  const [statData, setStatData] = useState({ left: {}, right: {} });
  const [loading, setLoading] = useState(false);
  const [dropdownsLocal, setDropDownsLocal] = useState(dropdowns)

  const {metric_id, metric_stat} = get_metric_info_from_identifier(metric_identifier_with_stat);
  const metric_type = metric_id?.split('-')?.[0];

  const { ready: permissionsReady, isDenied } = useDataAccess();
  const denied = isDenied(metric_type);

  const metric = useMemo(() => {
    return [...Object.values(all_metric_types).flat(), ...(helmet_metrics ?? [])]
      .find((metric) => getDropdownKey(metric, "metricId") === metric_id);
  }, [all_metric_types, helmet_metrics, metric_id]);

  useEffect(() => {
    if (dropdowns.length > 0) {
      if (dropdowns[0]?.children) {
        setDropDownsLocal(dropdowns.map((dropdownLocal) => {
          if (dropdownLocal.key === 'gps') {
            return {...dropdownLocal, children: dropdownLocal.children.filter((child) => {
                return metrics.find((metric) => metric.name === child.label)?.visible
              })
            }
          }
          return dropdownLocal
        }))
      } else {
        setDropDownsLocal(dropdowns.filter(dropdown => {
          const gpsMetrics = all_metric_types?.gps
          const gpsMetricNames = gpsMetrics?.map((metric) => metric.name)
          if (gpsMetricNames?.includes(dropdown.label)) {
            // means dropdown label is for a GPS metric = we check visibility
            return gpsMetrics.find((metric) => metric.name === dropdown.label)?.visible
          }
          else {
            // naive search to go through all metrics, and then check whether the metric is
            // get all metrics from keys other than 'gps'
            const nonGpsMetrics = Object.entries(all_metric_types)
                .filter(([key]) => key !== 'gps')
                .flatMap(([_, value]) => value);

            return nonGpsMetrics
          }
        }))
      }
    }
  }, [dropdowns])

  const fetchSingleStatTileData = useCallback((id) => {
    if (!startDate || !endDate) return '-';
    if (type === "player" && !playerId) return '-';

    // Null when the stat tiles API cannot express the tile: the legacy endpoint answers it.
    const v2Request = dispatch(requestStatTile({
      identifier: id, comparisonKey: option, startDate, endDate, type, playerId,
      positionNames: position, playerGroupIds: player_group, positionEntities,
    }));
    if (v2Request) return v2Request;

    const conditionalParams = type === "player"
      ? { player_id: playerId }
      : { ...(position && { position }), ...(player_group && { player_group }) };

    return trainingAPI.getSingleStatTileData({
      unique_identifier: id,
      comparison_key: option,
      end_date: endDate,
      start_date: startDate,
      ...conditionalParams,
    });
  }, [option, startDate, endDate, playerId, type, position, player_group, positionEntities, dispatch]);

  const fetchStatTileData = useCallback(async () => {
    if (!metric_identifier_with_stat || !isAssignedMetric(metric_identifier_with_stat, metrics)) {
      setStatData({});
      return;
    }
    // A saved tile can still hold a metric from a stream the user cannot view.
    // The backend answers 403 for it, so do not send the request.
    if (!permissionsReady || denied) {
      setStatData({});
      return;
    }

    setLoading(true);
    try {
      const statTileData = await fetchSingleStatTileData(metric_identifier_with_stat);
      setStatData(statTileData);
    } catch (e) {
      console.error("Error fetching stat tile data:", e);
      setStatData('-');
    } finally {
      setLoading(false);
    }
  }, [metric_identifier_with_stat, fetchSingleStatTileData, permissionsReady, denied]);

  useEffect(() => {
    fetchStatTileData();
  }, [fetchStatTileData, positionFilter]);

  const handleMenuClick = useCallback(({ key }) => {
    onChange(`${key}-avg`);
  }, [onChange]);

  const getStatButtonText = () => {
    if (metric_stat == "avg") { return "A" }
    if (metric_stat == "max") { return "B" }
    if (metric_stat == "sum") { return "C" }
    return "A"
  }

  const getStatButtonColor = () => {
    if (metric_stat == "avg") { return '#1790ff'}
    if (metric_stat == "max") { return '#008000'}
    if (metric_stat == "sum") { return '#800080' }
    return '#1790ff'
  }

  const handleToggleStat = useCallback(() => {
    // Cycling the stat on a tile with no metric yields "--avg", a request the
    // backend can only reject.
    if (!metric) return;
    // A helmet metric lists its aggregations, and the helmet registry names max `best`. A count has no best.
    const cycle = ["avg", "max", "sum"].filter((s) => !metric.aggregations || metric.aggregations.includes(s === "max" ? "best" : s));
    const next = cycle[(cycle.indexOf(metric_stat) + 1) % cycle.length];
    onChange(`${metric_id}-${next}`);
  }, [onChange, metric, metric_id, metric_stat]);

  const title = !metric ? "Choose Metric" : getMetricDropdownLabel(metric);

  return (
      <Card
        bodyStyle={{ padding: "5px 0px 0px 0px", display: 'flex', flexDirection: 'column', height: '100%' }}
        style={{
          width: innerWidth < 1800? '100%' : 320,
          minWidth:270,
          maxWidth:320,
          height: 115,//type=="team" ? 115 : innerWidth < 1800 ? 115 : 120,
          paddingBottom: 0, //type == "team" ? 0 : innerWidth < 1800? 25 : 0,
          borderRadius: 5,
          borderColor: "#cccccc",
          position: "relative",
        }}
        hoverable
        className={`${metric_type}-stat-tile`}
			  itemx={metric?.name}
      >
        <Row>
          <Dropdown
            menu={{
              items: dropdownsLocal || [],
              onClick: handleMenuClick,
            }}
            trigger={["hover"]}
          >
            <Button
                style={{
                  fontSize: 16,
                  height: "auto",
                  whiteSpace: "nowrap",
                  width: innerWidth < 1800? '100%' : 320,
                  maxWidth: 280,
                  minWidth: 160,
                  textAlign: "left",
                  justifyContent: "flex-start",
                  overflow: "hidden",
                  marginLeft: '2px'
                }}
                size="small"
                type="text"
              >
                <div className={title.length > 33 ? "labelContainer": 'none'}> 
                  <span style={{width: window.innerWidth < 1800? (240/1800)*window.innerWidth : 280}}>{title}</span>
                  </div>

              </Button>
          </Dropdown>
          <Tooltip 
            title={(<span>A: Average <br></br> B: Best <br></br> C: Cumulative</span>)}
            >
          <Button 
            type="primary" 
            size="small" 
            shape="round"
            style={{ 
              position: 'absolute', 
              right: '4px',
              backgroundColor: getStatButtonColor(),
              borderWidth: 0,
              padding: '0px 8px',
              borderRadius: '5px',
            }}
            onClick={handleToggleStat}
            disabled={!metric}
            value={metric_stat}
            >
            {getStatButtonText()}
            </Button>
            </Tooltip>
            
        </Row>
        <Row 
          style={{ 
            overflow: 'auto',
            marginTop: '0px', 
            minHeight: 60, 
            height: '100%',
            paddingLeft: '10px',
            '::WebkitScrollbar': {
              display: 'none'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            width: "87%"
          }} 
          wrap={true}
        >
          {denied ? (
            <Typography.Text type="secondary">
              <LockOutlined /> {NO_PERMISSION_LABEL}
            </Typography.Text>
          ) : loading ? (
            <Spin />
          ) : (
            statData.length !== 0 && (<>
              <CompactStatisticNumber
                metricStat={statData}
                dynamicFontSizing={true}
                isAsym={statData.is_asym}
                show_delta={metric_stat !== "sum" && option !== "selected_date_range"}
              />
            </>)
          )}
        </Row>
        { !denied && (typeof statData.latest_value) !== 'string' && (type == "player") ? (
          <Tooltip title={(<span>T: Team Average <br></br> P: Positional Average</span>)}>
          <div
            style={{
              position: "absolute",
              paddingTop: innerWidth < 1500? "5px": "0px",
              bottom: "5px",
              right: "10px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <StatValue label="T" value={statData?.team?.value} color={statData?.team?.color} isAsym={statData?.is_asym} />
            <StatValue label="P" value={statData?.pos?.value} color={statData?.pos?.color} isAsym={statData?.is_asym} />
          </div>
        </Tooltip>
        ) : null }
      </Card>
    );
};

const StatValue = ({ label, value, color, isAsym=false }) => {

  const fontSize = isAsym ? 11 : 15;
  const lineHeight = isAsym ? 0.9 : 1.2;

  return (
  <div
    style={{
      color: color || 'inherit',
      marginBottom: '2px',
      lineHeight: lineHeight,
      fontSize: fontSize
    }}
  >
    {label}: {value ?? '-'}
  </div>
)};

export default React.memo(CompactStatTile);